import React, {
  useEffect,
  useState,
} from "react";
import { toast } from "sonner";

import { mail as mailApi } from "@/api";
import ActaModal from "@/components/ui/ActaModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatApiError } from "@/lib/api";


const EMPTY_ACCOUNT = {
  email: "",
  imap_host: "imap.mail.ovh.net",
  imap_port: 993,
  smtp_host: "smtp.mail.ovh.net",
  smtp_port: 465,
};


export default function UserMailSettingsDialog({ user, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [hasPendingConfiguration, setHasPendingConfiguration] = useState(false);
  const [account, setAccount] = useState({ ...EMPTY_ACCOUNT, email: user?.email || "" });

  const busy = loading || saving || testing;

  const applyState = (response) => {
    setConfigured(Boolean(response?.configured));
    setHasPendingConfiguration(Boolean(response?.hasPendingConfiguration));
    setAccount({
      ...EMPTY_ACCOUNT,
      email: response?.account?.email || user?.email || "",
      imap_host: response?.account?.imapHost || EMPTY_ACCOUNT.imap_host,
      imap_port: response?.account?.imapPort || EMPTY_ACCOUNT.imap_port,
      smtp_host: response?.account?.smtpHost || EMPTY_ACCOUNT.smtp_host,
      smtp_port: response?.account?.smtpPort || EMPTY_ACCOUNT.smtp_port,
    });
    setDirty(false);
  };

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const response = await mailApi.adminAccount(user.id);
        if (!cancelled) applyState(response);
      } catch (error) {
        if (!cancelled) toast.error(formatApiError(error?.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [user]);

  const updateField = (field, value) => {
    setAccount((current) => ({ ...current, [field]: value }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await mailApi.adminConfigureAccount(user.id, account);
      applyState(response);
      toast.success("Paramètres de messagerie enregistrés");
    } catch (error) {
      toast.error(formatApiError(error?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    if (dirty) {
      toast.error("Enregistrez les modifications avant de tester la connexion.");
      return;
    }

    setTesting(true);
    try {
      await mailApi.adminTestAccount(user.id);
      const response = await mailApi.adminAccount(user.id);
      applyState(response);
      toast.success("Connexion à la boîte mail réussie");
      await onSaved?.();
    } catch (error) {
      toast.error(formatApiError(error?.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const mobileUnsupported =
    typeof window !== "undefined"
    && window.matchMedia("(max-width: 767px)").matches;

  if (mobileUnsupported) return null;

  return (
    <ActaModal open onClose={onClose} title={`Messagerie — ${user.firstName || ""} ${user.lastName || ""}`}>
      {loading ? (
        <div className="label-eyebrow lirah-pulse">Chargement</div>
      ) : (
        <div className="space-y-5">
          <section className="space-y-3">
            <div>
              <div className="label-eyebrow">Configuration de la boîte</div>
              <div className="mt-1 text-xs text-[var(--do-muted)]">
                {configured
                  ? "Une configuration active existe. Toute modification reste en attente jusqu’à validation du test."
                  : "Configurez les paramètres techniques. L’utilisateur renseignera son propre mot de passe depuis Paramètres → Ma messagerie."}
              </div>
            </div>
            <Input type="email" value={account.email} disabled={busy} onChange={(event) => updateField("email", event.target.value)} placeholder="prenom@entreprise.fr" />
            <div className="grid gap-3 sm:grid-cols-[1fr_110px]">
              <Input value={account.imap_host} disabled={busy} onChange={(event) => updateField("imap_host", event.target.value)} placeholder="Serveur IMAP" />
              <Input type="number" value={account.imap_port} disabled={busy} onChange={(event) => updateField("imap_port", Number(event.target.value))} />
            </div>
            <div className="grid gap-3 sm:grid-cols-[1fr_110px]">
              <Input value={account.smtp_host} disabled={busy} onChange={(event) => updateField("smtp_host", event.target.value)} placeholder="Serveur SMTP" />
              <Input type="number" value={account.smtp_port} disabled={busy} onChange={(event) => updateField("smtp_port", Number(event.target.value))} />
            </div>
          </section>

          <section className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4">
            <div className="label-eyebrow">Mot de passe</div>
            <p className="mt-1 text-xs text-[var(--do-muted)]">
              Le mot de passe appartient à l’utilisateur. Il n’est jamais affiché ni saisi ici par l’administrateur. L’utilisateur le gère depuis Paramètres → Ma messagerie.
            </p>
          </section>

          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="actaSecondary" disabled={busy} onClick={onClose}>Fermer</Button>
            <Button type="button" variant="actaSecondary" disabled={busy || dirty || (!configured && !hasPendingConfiguration)} onClick={testConnection}>{testing ? "Test…" : "Tester la connexion"}</Button>
            <Button type="button" variant="actaPrimary" disabled={busy || !dirty} onClick={save}>{saving ? "Enregistrement…" : "Enregistrer"}</Button>
          </div>
        </div>
      )}
    </ActaModal>
  );
}
